import axiosLib, { AxiosError, AxiosRequestConfig } from "axios";
import { refreshToken } from "~/api/account/requests";
import { MaxRetriesError } from "~/helpers/errors";
import {
  getJWT,
  setJWT,
  getRefreshToken,
  setRefreshToken,
} from "~/helpers/auth";
import { paths } from "~/router/paths";
import { signOutExcludedPaths } from "~/constants/paths";

import { endpointsToExcludeTheJWT } from "~/api/constants";
import { isAmazonS3Url } from "~/helpers/url-validation";
const apiURL: string = import.meta.env.VITE_API_URL;

export const axios = axiosLib.create({
  timeout: 60000,
});

const getURL = (path: string) => `${apiURL}/${path}`;

const handleRequest = (config: AxiosRequestConfig): any => {
  const isAuthEndpoint = endpointsToExcludeTheJWT.find((endpoint) =>
    config.url?.includes(endpoint)
  );
  const isS3Endpoint = isAmazonS3Url(config.url);

  if (isS3Endpoint || isAuthEndpoint) {
    return config;
  }

  const jwt = getJWT();
  if (jwt && config.headers) config.headers.Authorization = `Bearer ${jwt}`;
  return config;
};

axios.interceptors.request.use(handleRequest);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Datadog will auto-capture errors via forwardConsoleLogs
    console.error("API Request Failed", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
    });
    return Promise.reject(error);
  }
);

export const forceSignOut = () => {
  // Always drop the invalid tokens, even when the redirect below is skipped,
  // so route guards and the next page load see the signed-out state.
  setJWT("");
  setRefreshToken("");

  if (
    signOutExcludedPaths.find((path) =>
      new RegExp(`^${path}`).test(window.location.pathname)
    )
  ) {
    return; // Already on a guest page; no redirect needed.
  }

  window.location.href = paths.auth.login;
};

let refreshPromise: Promise<string | undefined> | null = null;

const refreshAccessToken = (refresh: string): Promise<string | undefined> => {
  if (!refreshPromise) {
    refreshPromise = refreshToken({ refresh })
      .then((res) => res?.data?.access)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const handleErrorWrapper = async <T = any>(
  next: () => Promise<T>,
  retries: number = 5
): Promise<T> => {
  if (retries <= 0) {
    forceSignOut();
    throw new MaxRetriesError("Max retries reached");
  }

  try {
    return await next();
  } catch (err: unknown) {
    if (err instanceof AxiosError) {
      if (err.response) {
        const { status } = err.response;
        const refresh = getRefreshToken();

        if (status === 402) {
          localStorage.setItem("plansModal", "true");
        }

        if (status === 401) {
          if (refresh) {
            if (err?.config?.url?.includes("/token/refresh/")) {
              forceSignOut();
              return Promise.reject(err);
            }

            let access: string | undefined;
            try {
              access = await refreshAccessToken(refresh);
            } catch {
              forceSignOut();
              return Promise.reject(err);
            }

            if (!access) {
              forceSignOut();
              return Promise.reject(err);
            }

            setJWT(access);
            return handleErrorWrapper(next, retries - 1);
          } else if (!err?.config?.url?.includes("/auth/")) {
            forceSignOut();
            return Promise.reject(err);
          }
        }
      }
    }

    return Promise.reject(err);
  }
};

export const sendGet = async (path: string, params?: Object): Promise<any> => {
  return handleErrorWrapper(async () => {
    return axios.get(getURL(path), { params });
  });
};

export const sendPost = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.post(getURL(path), params);
  });
};

export const sendPut = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.put(getURL(path), params);
  });
};

export const sendDelete = async (path: string, params: Object) => {
  return handleErrorWrapper(async () => {
    return axios.delete(getURL(path), params);
  });
};

export const sendStreamingPost = async (
  path: string,
  params?: Object,
  onProgress?: (progress: number, data: any) => void
) => {
  return handleErrorWrapper(async () => {
    const response = await axios.post(getURL(path), params, {
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.event.target.responseText && onProgress) {
          const chunks = progressEvent.event.target.responseText
            .split("\n")
            .filter(Boolean);

          chunks.forEach((chunk: any) => {
            try {
              const data = JSON.parse(chunk);
              onProgress(data.progress || 0, data);
            } catch (e) {
              console.error("Error parsing progress chunk:", e);
            }
          });
        }
      },
    });
    return response.data;
  });
};
